"use client";

import { useEffect, useState } from "react";

/**
 * Prices are stored in rupees. India sees rupees; everywhere else sees dollars at one flat rate (never a live FX rate;
 * the gap covers payment-provider cross-border fees). Country comes from our CDN's view of the visitor's IP, so a VPN counts;
 * only when the CDN gives no country do we fall back to the browser's time zone.
 */
export const INR_PER_USD = 100;
export const INDIA_COUNTRIES = ["IN"];
const INDIA_TIMEZONES = ["Asia/Kolkata", "Asia/Calcutta"];

export type Currency = "INR" | "USD";

let known: Currency | null = null;
let inflight: Promise<Currency> | null = null;

function fromTimezone(): Currency {
	try {
		return INDIA_TIMEZONES.includes(Intl.DateTimeFormat().resolvedOptions().timeZone) ? "INR" : "USD";
	} catch {
		return "INR";
	}
}

function resolveCurrency(): Promise<Currency> {
	if (known) return Promise.resolve(known);
	inflight ??= fetch("/api/country", { cache: "no-store" })
		.then((r) => (r.ok ? r.json() : { country: "" }))
		.then((j: { country?: string }) => (j.country ? (INDIA_COUNTRIES.includes(j.country) ? "INR" : "USD") : fromTimezone()))
		.catch(() => fromTimezone())
		.then((c) => (known = c));
	return inflight;
}

/** The display currency, or null until we know (so a price never flashes the wrong symbol). */
export function useCurrency(): Currency | null {
	const [currency, setCurrency] = useState<Currency | null>(known);
	useEffect(() => {
		let live = true;
		void resolveCurrency().then((c) => live && setCurrency(c));
		return () => { live = false; };
	}, []);
	return currency;
}

export function formatMoney(rupees: number, currency: Currency): string {
	if (currency === "INR") return `₹${rupees.toLocaleString("en-IN")}`;
	const usd = rupees / INR_PER_USD;
	return `$${usd.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(usd) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** A price in rupees shown in the visitor's currency. Takes its space while the currency is being found. */
export function Money({ inr, className }: { inr: number; className?: string }) {
	const c = useCurrency();
	return (
		<span className={className} data-currency={c ?? "pending"} style={c ? undefined : { visibility: "hidden" }}>
			{formatMoney(inr, c ?? "INR")}
		</span>
	);
}
